import React, { useRef, useMemo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from '~/shared/utils/translation';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useMemberAccountInformationSilent } from '../../hooks/useMemberAccountInformationSilent';
import { useLoyaltyContext } from '~/shared/hooks/loyalty';
import { usePage } from '~/templates/pages';
import Typography from '~/shared/components/Typography/Typography';
import Breadcrumb from '~/shared/components/Breadcrumb/Breadcrumb';
import Image from 'next/image';
import ChevronLeft from '~/icons/chevron-left.svg';
import Link from 'next/link';
import styles from './ProfileSidebar.module.scss';
import TabItem from './TabItem';
import ImercoPlusDrawer from '../ImercoPlusDrawer/ImercoPlusDrawer';

import ProfileLogoutIcon from '~/icons/profile-logout.svg';
import ProfileReceiptsIcon from '~/icons/profile-receipts.svg';
import ProfileRewardsIcon from '~/icons/profile-rewards.svg';
import ProfileVouchersIcon from '~/icons/profile-vouchers.svg';
import ProfileSettingsIcon from '~/icons/profile-settings.svg';
import ProfileOverviewIcon from '~/icons/profile-overview.svg';

export type ProfileTabType = 'overview' | 'bonusrewards' | 'vouchers' | 'purchases' | 'settings';

type MobileBackButtonProps = {
    href: string;
    ariaLabel: string;
};

/**
 * Mobile back button - shown instead of full sidebar on mobile sub-tabs
 */
const MobileBackButton: React.FC<MobileBackButtonProps> = ({ href, ariaLabel }) => {
    return (
        <Link href={href} className={styles.mobileBackButton} aria-label={ariaLabel}>
            <ChevronLeft />
        </Link>
    );
};

type ProfileSidebarCTAProps = {
    onClick: () => void;
};

const ProfileSidebarCTA: React.FC<ProfileSidebarCTAProps> = ({ onClick }) => {
    const { translate } = useTranslation();
    return (
        <button className={styles.sidebarCTA} onClick={onClick}>
            <Image src={`/assets/logo-imerco-plus.png`} alt="Imerco Plus" width={63} height={64} />
            <label>
                <Typography variant="paragraph-S" weight="medium" as="p">
                    {translate('loyalty.imercoPlus.cta.title')}
                </Typography>
                <Typography variant="paragraph-XS" weight="regular" as="span">
                    {translate('loyalty.imercoPlus.cta.description')}
                </Typography>
            </label>
        </button>
    );
};

const ProfileSidebar: React.FC = () => {
    const { translate } = useTranslation();
    const router = useRouter();
    const customer = useCustomer();
    const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
    const page = usePage();
    const pageBreadcrumb = page.breadcrumb;
    // Get page title from meta.title or fallback to last breadcrumb item
    const pageTitle =
        page.meta?.title ||
        pageBreadcrumb?.items?.[pageBreadcrumb.items.length - 1]?.text ||
        translate('loyalty.menu.overview');
    const { canFetchLoyaltyData, businessCentralId, isPlusMember } = useLoyaltyContext();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const profileOverviewHref =
        pageBreadcrumb?.items?.[pageBreadcrumb.items.length - 1]?.url || '/my-profile';

    // Fetch member account information for badge counts
    // Using silent version to suppress error toasts when endpoint is unavailable
    // The query is automatically disabled when canFetchLoyaltyData is false (via useLoyaltyContext)
    // If the query fails (endpoint unavailable), we'll hide loyalty tabs
    const {
        data: memberAccountData,
        error: memberAccountError,
        isError: isMemberAccountError,
        isLoading: isMemberAccountLoading,
        isFetching: isMemberAccountFetching,
    } = useMemberAccountInformationSilent();
    const memberAccount = memberAccountData?.value?.[0];

    const isQuerySettled = !isMemberAccountLoading && !isMemberAccountFetching;
    const shouldShowLoyaltyTabs =
        canFetchLoyaltyData && isQuerySettled && !isMemberAccountError && !!memberAccountData;

    // Debug logging
    useEffect(() => {
        console.log('ProfileSidebar Debug:', {
            customer: {
                customerId: customer.customerId,
                email: customer.email,
                isPlusMember: customer.isPlusMember,
                businessCentralId: customer.businessCentralId,
            },
            loyaltyContext: {
                canFetchLoyaltyData,
                businessCentralId,
                isPlusMember,
            },
            queryState: {
                memberAccountData,
                memberAccount,
                memberAccountError,
                isMemberAccountError,
                isMemberAccountLoading,
                isMemberAccountFetching,
                isQuerySettled,
            },
            shouldShowLoyaltyTabs,
        });
    }, [
        customer,
        canFetchLoyaltyData,
        businessCentralId,
        isPlusMember,
        memberAccountData,
        memberAccount,
        memberAccountError,
        isMemberAccountError,
        isMemberAccountLoading,
        isMemberAccountFetching,
        isQuerySettled,
        shouldShowLoyaltyTabs,
    ]);

    // Get active tab from query param, default to 'overview'
    const activeTab = (router.query.tab as ProfileTabType) || 'overview';

    // Redirect to overview if user tries to access a disabled loyalty tab
    useEffect(() => {
        if (!shouldShowLoyaltyTabs) {
            const loyaltyTabs: ProfileTabType[] = ['bonusrewards', 'vouchers', 'purchases'];
            if (loyaltyTabs.includes(activeTab)) {
                router.replace(profileOverviewHref, undefined, { scroll: false });
            }
        }
    }, [shouldShowLoyaltyTabs, activeTab, router, profileOverviewHref]);

    // Build breadcrumb with current tab as last item
    const breadcrumbItems = useMemo(() => {
        // Tab name mapping for breadcrumb
        const tabNames: Record<ProfileTabType, string> = {
            overview: translate('loyalty.menu.overview'),
            bonusrewards: translate('loyalty.menu.bonusrewards'),
            vouchers: translate('loyalty.menu.vouchers'),
            purchases: translate('loyalty.menu.purchases'),
            settings: translate('loyalty.menu.settings'),
        };

        // Get base items from page breadcrumb or create default
        let baseItems = pageBreadcrumb?.items || [
            { text: translate('common.home'), url: '/', title: translate('common.home') },
            { text: pageTitle, url: profileOverviewHref, title: pageTitle },
        ];

        // If baseItems came from pageBreadcrumb, ensure the first item (home) is translated
        if (pageBreadcrumb?.items && baseItems.length > 0 && baseItems[0]?.url === '/') {
            baseItems = [
                {
                    ...baseItems[0],
                    text: translate('common.home'),
                    title: translate('common.home'),
                },
                ...baseItems.slice(1),
            ];
        }

        // If on overview, just return base items
        if (activeTab === 'overview') {
            return baseItems;
        }

        // Get all items except the last one (which might be the current tab or profile page)
        const parentItems = baseItems.slice(0, -1);

        // Ensure "Min Profil" is included before the tab
        return [
            ...parentItems,
            {
                text: pageTitle || baseItems[baseItems.length - 1]?.text || 'Min Profil',
                url: profileOverviewHref,
                title: pageTitle || baseItems[baseItems.length - 1]?.title || 'Min Profil',
            },
            {
                text: tabNames[activeTab],
                url: `${profileOverviewHref}?tab=${activeTab}`,
                title: tabNames[activeTab],
            },
        ];
    }, [pageBreadcrumb, activeTab, pageTitle, profileOverviewHref, translate]);

    // Tab configuration - filter out loyalty tabs when endpoint is unavailable
    const tabs = useMemo(() => {
        const allTabs = [
            {
                id: 'overview' as ProfileTabType,
                label: translate('loyalty.menu.overview'),
                icon: <ProfileOverviewIcon />,
            },
            {
                id: 'bonusrewards' as ProfileTabType,
                label: translate('loyalty.bonusRewardsAndPoints'),
                icon: <ProfileRewardsIcon />,
                badge: {
                    value: memberAccount?.sumOfCoupons || 0,
                    unit: 'kr.',
                },
            },
            {
                id: 'vouchers' as ProfileTabType,
                label: translate('loyalty.menu.vouchers'),
                icon: <ProfileVouchersIcon />,
                badge: {
                    value: memberAccount?.qtyOfVouchers || 0,
                    unit: translate('common.quantityUnit'),
                },
            },
            {
                id: 'purchases' as ProfileTabType,
                label: translate('loyalty.purchasesAndReceipts'),
                icon: <ProfileReceiptsIcon />,
            },
            {
                id: 'settings' as ProfileTabType,
                label: translate('loyalty.myInformation'),
                icon: <ProfileSettingsIcon />,
            },
        ];

        // Hide loyalty tabs (bonusrewards, vouchers, purchases) when endpoint is unavailable
        // This includes when canFetchLoyaltyData is false OR when the query fails
        if (!shouldShowLoyaltyTabs) {
            return allTabs.filter(
                (tab) => !['bonusrewards', 'vouchers', 'purchases'].includes(tab.id),
            );
        }

        return allTabs;
    }, [shouldShowLoyaltyTabs, memberAccount, translate]);

    // Handle tab navigation
    const navigateToTab = (tabId: ProfileTabType) => {
        router.push(
            `${profileOverviewHref}${tabId === 'overview' ? '' : `?tab=${tabId}`}`,
            undefined,
            {
                scroll: false,
            },
        );
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            const nextIndex = (index + 1) % (tabs.length + 1); // +1 for logout button
            tabsRef.current[nextIndex]?.focus();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const prevIndex = (index - 1 + tabs.length + 1) % (tabs.length + 1); // +1 for logout button
            tabsRef.current[prevIndex]?.focus();
        }
    };

    // Handle logout
    const handleLogout = async () => {
        await customer.logout();
        window.location.href = '/';
    };

    // Render both components, CSS controls visibility based on screen size and tab
    return (
        <div className={styles.sidebarWrapper} data-active-tab={activeTab}>
            <div className={styles.sidebar}>
                {breadcrumbItems.length > 1 && (
                    <nav className={styles.breadcrumbContainer} aria-label="Breadcrumb">
                        <Breadcrumb items={breadcrumbItems} />
                    </nav>
                )}

                {/* Mobile back button - shown on mobile when not on overview */}
                <MobileBackButton
                    href={profileOverviewHref}
                    ariaLabel={translate('loyalty.menu.overview')}
                />

                <div className={styles.sidebarTabs} role="tablist" aria-label="Profile navigation">
                    {tabs.map((tab, index) => (
                        <TabItem
                            key={tab.id}
                            ref={(el) => (tabsRef.current[index] = el)}
                            id={tab.id}
                            label={tab.label}
                            icon={tab.icon}
                            badge={tab.badge}
                            isActive={activeTab === tab.id}
                            onClick={() => navigateToTab(tab.id)}
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                        />
                    ))}
                </div>
                <div className={styles.logoutButtonWrapper}>
                    <TabItem
                        ref={(el) => (tabsRef.current[tabs.length] = el)}
                        id="logout"
                        label={translate('login.logout')}
                        icon={<ProfileLogoutIcon />}
                        onClick={handleLogout}
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, tabs.length)}
                    />
                </div>
                {shouldShowLoyaltyTabs && (
                    <ProfileSidebarCTA onClick={() => setIsDrawerOpen(true)} />
                )}
            </div>
            <ImercoPlusDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
        </div>
    );
};

export default ProfileSidebar;
